import {
  COURSE_HOUSING_TYPES_MAP,
  COURSE_REQUIRED_DOCS,
  COURSE_TYPES_MAP,
  courseDetailsConfig,
  LANGUAGES,
} from "../constants";
import {
  IAccommodationFeeViewModel,
  IApplicationFormsFrontEndModel,
  ICourseBackEndViewModel,
  ICourseViewModel,
  IDetailFileFrontEndModel,
  IDetailsViewModel,
  IGetBranchBackEndModel,
  IOtherFeesFrontEndModel,
} from "../types";
import {
  calculateFinancialSupport,
  getBeautifiedDuration,
  toCurrency,
} from "../../../helpers";
import { calcFeeWithDiscount, getBranchesNames } from "./helperFunctions";
import { getData } from "country-list";

const countries: { code: string; name: string }[] = getData();

export function parseToViewCourse({
  backEndData,
  branches,
}: {
  backEndData: ICourseBackEndViewModel;
  branches: IGetBranchBackEndModel[];
}): ICourseViewModel {
  const language = LANGUAGES.find((l) => l.id === backEndData.language)!.name;

  // Required Documents -------------------------------------
  const requiredDocNames = backEndData.requiredDocs.map((doc) => {
    const existedDoc = COURSE_REQUIRED_DOCS.find((d) => d.name === doc.name);

    return existedDoc?.longName || doc.name;
  });

  // Application Link ---------------------------------------
  let applicationLink;

  if (backEndData.applicationLink) {
    applicationLink = backEndData.applicationLink.includes("http", 0)
      ? backEndData.applicationLink
      : "http://" + backEndData.applicationLink;
  }

  // Housing Type -------------------------------------------
  let housingTypes;

  if (backEndData.housingTypes?.length) {
    housingTypes = backEndData.housingTypes
      .sort((a, b) => +a - +b)
      .map((type) => COURSE_HOUSING_TYPES_MAP[type]);
  }

  // Restricted Countries -----------------------------------
  let restrictedCountries;

  if (backEndData.restrictedCountries?.length) {
    restrictedCountries = backEndData.restrictedCountries.map(
      (code) => countries.find((c) => c.code === code)!.name,
    );
  }

  // Finance --------------------------------------------------
  const currency = backEndData.currency;
  const price = backEndData.price;

  const {
    feeWithCurrency: priceWithCurrency,
    calcFeeWithCurrency: calcPriceWithCurrency,
    discountWithCurrency: priceDiscountWithCurrency,
    hasSupport: hasPriceSupport,
  } = calcFeeWithDiscount({
    fee: price,
    hasFeeDiscount: backEndData.hasPriceDiscount,
    feeDiscountType: backEndData.priceDiscountType,
    feeDiscountAmount: backEndData.priceDiscountAmount,
    currency,
  });

  // Commission
  const commissionAmount = calculateFinancialSupport(
    price,
    true,
    backEndData.commissionType,
    +backEndData.commissionAmount,
  );

  // Accommodation Fees
  const accommodationFees: IAccommodationFeeViewModel[] = [];

  if (backEndData.residenceAmount) {
    accommodationFees.push({
      label: "Residence",
      amount: toCurrency(backEndData.residenceAmount, currency, " ", "code"),
    });
  }

  if (backEndData.homestayAmount) {
    accommodationFees.push({
      label: "Homestay",
      amount: toCurrency(backEndData.homestayAmount, currency, " ", "code"),
    });
  }

  if (backEndData.insuranceAmount) {
    accommodationFees.push({
      label: "Insurance",
      amount: toCurrency(backEndData.insuranceAmount, currency, " ", "code"),
    });
  }

  // Other Fees -----------------------------------------------
  let otherFeesSum = 0;
  const otherFees: IOtherFeesFrontEndModel[] = [];

  backEndData.otherFees.forEach((fee) => {
    otherFees.push({
      name: fee.name,
      amount: toCurrency(fee.amount, currency, " ", "code"),
    });

    otherFeesSum += fee.amount;
  });

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    discountWithCurrency: otherFeesDiscountWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithDiscount({
    fee: otherFeesSum,
    hasFeeDiscount: backEndData.hasOtherFeesDiscount,
    feeDiscountType: backEndData.otherFeesDiscountType,
    feeDiscountAmount: +(backEndData.otherFeesDiscountAmount ?? ""),
    currency,
  });

  //Details
  const details: IDetailsViewModel[] = [];

  backEndData.details
    .sort((a, b) => a.type - b.type)
    .forEach((config) => {
      const foundDetail = courseDetailsConfig.find(
        (detail) => detail.type === config.type,
      );

      if (foundDetail) {
        details.push({
          type: config.type,
          typeName: foundDetail.name,
          content: config.content,
        });
      }
    });

  //Detail Files
  const detailFiles: IDetailFileFrontEndModel[] = backEndData.detailFiles.map(
    (file) => ({
      id: file.fileId,
      name: file.name!,
      size: file.size!,
      path: file.path!,
    }),
  );

  //Application Forms
  const applicationForms: IApplicationFormsFrontEndModel[] =
    backEndData.applicationForms.map((file) => ({
      id: file.fileId,
      name: file.name!,
      size: file.size!,
      preview: file.path!,
    }));

  return {
    language: language,
    category: backEndData.category,
    courseType: COURSE_TYPES_MAP[backEndData.courseType],
    minAge: backEndData.minAge.toString(),
    maxAge: backEndData.maxAge?.toString() ?? "N/A",
    branches: getBranchesNames(branches, backEndData.branches),
    requiredDocs: requiredDocNames,
    applicationLink: applicationLink,
    applicationForms: applicationForms,
    courseDuration: getBeautifiedDuration(
      "Course",
      backEndData.durationUnit,
      backEndData.durationAmount,
    ),
    housingTypes: housingTypes,
    restrictedCountries: restrictedCountries,
    price: priceWithCurrency,
    priceDuration: getBeautifiedDuration(
      "Course",
      backEndData.priceDurationUnit,
      backEndData.priceDurationAmount ?? "",
    ),
    commissionAmount: toCurrency(commissionAmount, currency, " ", "code"),
    otherFees: otherFees,
    accommodationFees: accommodationFees,
    priceDiscount: priceDiscountWithCurrency,
    calculatedPrice: calcPriceWithCurrency,
    otherFeesSum: otherFeesWithCurrency,
    otherFeesDiscount: otherFeesDiscountWithCurrency,
    calculatedOtherFees: calcOtherFeesWithCurrency,
    hasPriceSupport: hasPriceSupport,
    hasOtherFeesSupport: hasOtherFeesSupport,
    details: details,
    detailFiles: detailFiles,
  };
}
